import { getAccessToken } from '../utils/storage';
import { authFetch, getApiUrl } from '../utils/api';
import { friendlyApiError } from '../utils/apiErrors';

export interface LMSMasterColumn {
  key: string;
  label: string;
  numeric?: boolean;
  hidden?: boolean;
}

export interface LMSMasterReportResponse {
  key: string;
  title: string;
  rows: Record<string, unknown>[];
  columns: LMSMasterColumn[];
  summary?: {
    row_count?: number;
    numeric_totals?: Record<string, number>;
    numeric_averages?: Record<string, number>;
    filter_options?: Record<string, Array<string | number>>;
  };
}

const makeHeaders = (): Record<string, string> => {
  const token = getAccessToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  return headers;
};

export const fetchLMSMasterReport = async (
  reportKey: string,
  limit?: number,
  filters: Record<string, string | number | undefined> = {},
): Promise<LMSMasterReportResponse> => {
  const params = new URLSearchParams();
  if (limit !== undefined) {
    params.set('limit', String(limit));
  }
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && String(value).trim() !== '') {
      params.set(key, String(value));
    }
  });

  const queryString = params.toString();
  const response = await authFetch(getApiUrl(`/v1/lms-master/${reportKey}${queryString ? `?${queryString}` : ''}`), {
    method: 'GET',
    headers: makeHeaders(),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(friendlyApiError(response.status, errorData.detail, 'The LMS Master report'));
  }

  const data: LMSMasterReportResponse = await response.json();
  return {
    ...data,
    rows: Array.isArray(data.rows) ? data.rows : [],
    columns: Array.isArray(data.columns) ? data.columns : [],
  };
};
